import type {AppendMessage, ThreadMessage} from '@assistant-ui/core';
import {
  useExternalMessageConverter,
  useExternalStoreRuntime,
} from '@assistant-ui/core/react';
import type {
  HttpUserPrompt,
  PromptPart,
} from '@wandb/weave/lib/WbAgent/promptParts';
import {useWbAgentClient} from '@wandb/weave/lib/WbAgent/useWbAgentClient';
import {
  type Dispatch,
  type MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {buildThreadFromTurns} from './buildThread';
import {auiContentToStoreParts, toAUIMessage} from './messageConversions';
import {buildPromptContextParts, type PromptContextParams} from './pageContext';
import {extractErrorMessage} from './runtime/errors';
import type {RuntimeEvent} from './runtime/events';
import type {AgentRuntime} from './runtime/runtime';
import type {AgentChatAction} from './store/actions';
import {
  addUserMessage,
  beginLoadThread,
  createThread,
  failLoadThread,
  loadThread,
  setActiveThread,
  setThreadError,
  setThreadStatus,
} from './store/actions';
import {
  type AgentChatStore,
  useActiveThread,
  useActiveThreadLoadState,
  useAgentChatDispatch,
  useAgentChatStore,
} from './store/context';
import {getActiveBranch} from './store/messageTree';
import {selectThreadIdByRootTurnId} from './store/selectors';
import {
  type ContentPart,
  hasMeaningfulContent,
  type MessageNode,
  type PagePromptContent,
  type Thread,
  type WorkspacePageContent,
} from './store/thread';
import {StreamingBuffer} from './streamingBuffer';

/**
 * BuiltPrompt is the frontend representation of a user prompt produced by the
 * composer (or reconstructed from store content for re-runs). It pairs the
 * backend shape (`httpParts`) with the store shape (`storeParts`) so callers
 * can both POST to `/turns` and optimistically render in the message tree
 * without re-deriving either from the other.
 */
export interface BuiltPrompt {
  httpParts: HttpUserPrompt;
  storeParts: ContentPart[];
  plainText: string;
}

/**
 * useAgentChatRuntime makes an `ExternalStoreRuntime` for `assistant-ui`
 * backed by the agent chat store and the given {@link AgentRuntime} (if ready).
 * `params` is captured on every user message to ground the agent in the
 * caller's current route + page state.
 */
export function useAgentChatRuntime(
  runtime: AgentRuntime | null,
  params: PromptContextParams
): ReturnType<typeof useExternalStoreRuntime> {
  const dispatch = useAgentChatDispatch();
  const activeThread = useActiveThread();
  const {activeThreadId: storeThreadId, isLoading: isActiveThreadLoading} =
    useActiveThreadLoadState();
  const store = useAgentChatStore();

  // Use a ref so callbacks always read the latest `activeThread` value.
  const activeThreadRef = useRef(activeThread);
  activeThreadRef.current = activeThread;

  // Lag the thread-list adapter's thread ID by one tick on switch so it changes
  // in sync with real messages, not during the empty-messages transition tick.
  // This avoids a crash in AUI's `setAdapter` [1] where a new internal thread is
  // created from a stale factory before messages are updated.
  //
  // Both renders commit before paint via `useLayoutEffect`, so there's no
  // flicker.
  //
  // [1] https://github.com/assistant-ui/assistant-ui/issues/3652
  const [committedThreadId, setCommittedThreadId] = useState(storeThreadId);
  useLayoutEffect(() => {
    if (committedThreadId !== storeThreadId) {
      setCommittedThreadId(storeThreadId);
    }
  }, [storeThreadId, committedThreadId]);
  const isThreadTransitioning = committedThreadId !== storeThreadId;

  const isRunning =
    activeThread?.status === 'running' && !isThreadTransitioning;

  const activeBranch = useMemo(
    () =>
      activeThread && !isThreadTransitioning
        ? getActiveBranch(activeThread.messageTree)
        : [],
    [activeThread, isThreadTransitioning]
  );
  const threadError =
    activeThread?.status === 'error' ? activeThread.error : undefined;

  const metadata = useMemo(
    () => ({error: threadError ?? undefined}),
    [threadError]
  );

  const convertedMessages = useExternalMessageConverter<MessageNode>({
    callback: toAUIMessage,
    messages: activeBranch,
    isRunning,
    metadata,
  });

  // Used to cancel async generators if the runtime provider unmounts.
  const shutdownRef = useRef(new AbortController());

  useEffect(() => {
    const controller = shutdownRef.current;
    return () => {
      controller.abort();
    };
  }, []);

  const drainEvents = useCallback(
    async (
      threadId: string,
      generator: AsyncGenerator<RuntimeEvent, void, undefined>
    ): Promise<'idle' | 'error' | 'cancelled'> => {
      // Scope every dispatch to the run that produced it. The reducer drops
      // events whose runId already has an outcome in thread.runOutcomes.
      let runId = '';
      const buffer = new StreamingBuffer(event => {
        dispatch({
          type: 'applyRuntimeEvent',
          threadId,
          event,
          runId,
          timestamp: Date.now(),
        });
      });

      let outcome: 'idle' | 'error' | 'cancelled' = 'idle';
      try {
        for await (const event of generator) {
          if (event.type === 'run-started') {
            runId = event.runId;
          }
          if (event.type === 'run-error') {
            outcome = 'error';
          }
          if (event.type === 'run-cancelled') {
            outcome = 'cancelled';
          }
          buffer.push(event);
        }
      } finally {
        buffer.dispose();
      }

      return outcome;
    },
    [dispatch]
  );

  const withStatusDispatch = useCallback(
    async (
      threadId: string,
      run: () => Promise<'idle' | 'error' | 'cancelled'>
    ): Promise<void> => {
      dispatch(setThreadStatus(threadId, 'running'));
      dispatch(setThreadError(threadId, null));

      try {
        const result = await run();
        if (result === 'cancelled') {
          return;
        }
        if (result !== 'error') {
          dispatch(setThreadStatus(threadId, 'idle'));
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          dispatch(setThreadStatus(threadId, 'idle'));
        } else {
          dispatch(setThreadError(threadId, extractErrorMessage(err)));
          dispatch(setThreadStatus(threadId, 'error'));
        }
      }
    },
    [dispatch]
  );

  const startTurn = useCallback(
    async (
      threadId: string,
      opts: {userPrompt?: HttpUserPrompt; parentId?: string | null}
    ) => {
      if (!runtime) {
        dispatch(setThreadError(threadId, null));
        dispatch(setThreadStatus(threadId, 'idle'));
        return;
      }

      await withStatusDispatch(threadId, async () => {
        return drainEvents(
          threadId,
          runtime.runTurn({
            threadId,
            userPrompt: opts.userPrompt,
            parentId: opts.parentId,
            signal: shutdownRef.current.signal,
          })
        );
      });
    },
    [dispatch, runtime, shutdownRef, drainEvents, withStatusDispatch]
  );

  const abortThread = useCallback(
    (threadId: string) => {
      const runId = activeThreadRef.current?.currentRunId;
      if (runId) {
        dispatch({
          type: 'applyRuntimeEvent',
          threadId,
          event: {type: 'run-cancelled', runId},
          runId,
          timestamp: Date.now(),
        });
      }
      runtime?.cancel?.(threadId);
    },
    [runtime, dispatch, activeThreadRef]
  );

  const switchToThread = useSwitchToThread(
    runtime,
    dispatch,
    store,
    shutdownRef,
    drainEvents,
    withStatusDispatch
  );

  const {onNew, onEdit, onReload, onCancel} = useThreadCallbacks(
    activeThreadRef,
    dispatch,
    startTurn,
    abortThread,
    params
  );

  const onAddToolResult = useCallback(
    (options: {
      messageId: string;
      toolCallId: string;
      result: unknown;
      isError?: boolean;
    }) => {
      const thread = activeThreadRef.current;
      if (!thread?.id || !thread.currentRunId) {
        return;
      }

      dispatch({
        type: 'applyRuntimeEvent',
        threadId: thread.id,
        runId: thread.currentRunId,
        event: {
          type: 'tool-call-result',
          content: String(options.result),
          isError: options.isError ?? false,
          toolCallId: options.toolCallId,
        },
        timestamp: Date.now(),
      });
    },
    [dispatch, activeThreadRef]
  );

  const setMessages = useCallback(
    (messages: readonly ThreadMessage[]) => {
      const threadId = activeThreadRef.current?.id;
      if (!threadId || messages.length === 0) {
        return;
      }

      const targetNodeId = messages[messages.length - 1].id;
      if (targetNodeId !== activeThreadRef.current?.messageTree.currentNodeId) {
        dispatch({
          type: 'switchBranch',
          timestamp: Date.now(),
          targetNodeId,
          threadId,
        });
      }
    },
    [dispatch, activeThreadRef]
  );

  // Switch the thread-list ID in sync with real messages, not during the
  // empty-messages transition tick; see above comment.
  const threadListAdapter = useMemo(
    () => ({
      // AUI uses `??` to fall back to DEFAULT_THREAD_ID; '' bypasses that
      // fallback and corrupts AUI's thread-list state. Reachable when
      // switching from a summary history entry before its turns have loaded.
      threadId: committedThreadId ?? undefined,
      threads: committedThreadId
        ? [{id: committedThreadId, status: 'regular' as const}]
        : [],
      onSwitchToNewThread: () => dispatch(createThread()),
      onSwitchToThread: switchToThread,
    }),
    [committedThreadId, dispatch, switchToThread]
  );

  const auiRuntime = useExternalStoreRuntime({
    isDisabled: isActiveThreadLoading,
    messages: convertedMessages,
    setMessages,
    isRunning,
    onNew,
    onEdit,
    onReload,
    onCancel,
    onAddToolResult,
    adapters: {
      threadList: threadListAdapter,
    },
  });

  return auiRuntime;
}

type DrainEvents = (
  threadId: string,
  generator: AsyncGenerator<RuntimeEvent, void, undefined>
) => Promise<'idle' | 'error' | 'cancelled'>;

type WithStatusDispatch = (
  threadId: string,
  run: () => Promise<'idle' | 'error' | 'cancelled'>
) => Promise<void>;

/**
 * useSwitchToThread loads a historical conversation by its root turn ID, or
 * activates an already-loaded thread. Each new switch aborts any previous
 * in-flight fetch, so rapid switching is safe.
 */
function useSwitchToThread(
  runtime: AgentRuntime | null,
  dispatch: Dispatch<AgentChatAction>,
  store: AgentChatStore,
  shutdownRef: MutableRefObject<AbortController>,
  drainEvents: DrainEvents,
  withStatusDispatch: WithStatusDispatch
): (rootTurnId: string) => Promise<void> {
  // Thread switch may target a thread in any project; scope is overridden
  // per call below.
  const client = useWbAgentClient();
  const switchControllerRef = useRef<AbortController | null>(null);

  return useCallback(
    async (rootTurnId: string) => {
      const state = store.getState();
      const existingId = selectThreadIdByRootTurnId(state, rootTurnId);
      const existing = existingId ? state.threads[existingId] : undefined;
      if (existing && existing.loadStatus === 'loaded') {
        dispatch(setActiveThread(existing.id));
        return;
      }

      if (!client) {
        return;
      }

      // Abort any previous in-flight switch.
      switchControllerRef.current?.abort();
      const controller = new AbortController();
      switchControllerRef.current = controller;

      // Also abort if the component unmounts.
      shutdownRef.current.signal.addEventListener(
        'abort',
        () => controller.abort(),
        {once: true}
      );

      dispatch(beginLoadThread(rootTurnId));

      try {
        const turns = await client.get(
          '/turns',
          {
            // Fetch across all projects the user can read; the thread may live
            // in a project different from the current route.
            entity: undefined,
            project: undefined,
            root_turn_id: rootTurnId,
            sort_by: 'created_at',
            sort_desc: false,
            // TODO: Handle pagination for turns.
            limit: 100,
          },
          controller.signal
        );

        const thread = buildThreadFromTurns(rootTurnId, turns);
        dispatch(loadThread(thread));

        // Resume polling if the last turn is still in-progress.
        //
        // TODO: Re-use existing polling generator if one exists.
        if (
          thread.status === 'running' &&
          turns.length > 0 &&
          runtime?.resumeTurn
        ) {
          const lastTurn = turns[turns.length - 1];
          await withStatusDispatch(rootTurnId, async () => {
            const gen = runtime.resumeTurn!({
              turnId: lastTurn.id,
              threadId: rootTurnId,
              signal: controller.signal,
              initialSnapshot: lastTurn,
            });
            return drainEvents(rootTurnId, gen);
          });
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          return;
        }
        dispatch(failLoadThread(extractErrorMessage(err)));
      }
    },
    [
      client,
      runtime,
      dispatch,
      store,
      shutdownRef,
      drainEvents,
      withStatusDispatch,
    ]
  );
}

type StartTurn = (
  threadId: string,
  opts: {userPrompt?: HttpUserPrompt; parentId?: string | null}
) => Promise<void>;

/**
 * useThreadCallbacks creates all four AUI thread callbacks: `onNew`, `onEdit`,
 * `onReload`, and `onCancel`. `params` is the current route's nav params,
 * captured on each user message to build prompt context.
 */
function useThreadCallbacks(
  activeThreadRef: MutableRefObject<Thread | undefined>,
  dispatch: Dispatch<AgentChatAction>,
  startTurn: StartTurn,
  abortThread: (threadId: string) => void,
  params: PromptContextParams
) {
  const appendAndRunTurn = useCallback(
    async (threadId: string, prompt: BuiltPrompt, parentId: string | null) => {
      dispatch(addUserMessage(threadId, parentId, prompt.storeParts));
      await startTurn(threadId, {userPrompt: prompt.httpParts, parentId});
    },
    [dispatch, startTurn]
  );

  // Hold params in a ref so the callback identities stay stable across route
  // changes; otherwise AUI re-binds on every navigation.
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const onNew = useCallback(
    async (message: AppendMessage) => {
      const userBuilt =
        readBuiltPrompt(message) ?? builtPromptFromText(extractText(message));

      if (!hasMeaningfulContent(userBuilt.storeParts)) {
        return;
      }

      const currentThread = activeThreadRef.current;
      let threadId = currentThread?.id ?? null;
      if (!threadId) {
        const action = createThread();
        threadId = action.id;
        dispatch(action);
      }

      const parentId = currentThread?.messageTree.currentNodeId ?? null;
      await appendAndRunTurn(
        threadId,
        withPromptContext(userBuilt, paramsRef.current),
        parentId
      );
    },
    [dispatch, appendAndRunTurn, activeThreadRef]
  );

  const onEdit = useCallback(
    async (message: AppendMessage) => {
      const threadId = activeThreadRef.current?.id;
      if (!threadId) {
        return;
      }

      const userBuilt =
        readBuiltPrompt(message) ?? builtPromptFromText(extractText(message));

      if (!hasMeaningfulContent(userBuilt.storeParts)) {
        return;
      }

      await appendAndRunTurn(
        threadId,
        withPromptContext(userBuilt, paramsRef.current),
        message.parentId ?? null
      );
    },
    [appendAndRunTurn, activeThreadRef]
  );

  const onReload = useCallback(
    async (parentId: string | null, _config?: unknown) => {
      const thread = activeThreadRef.current;
      if (!thread) {
        return;
      }

      const node = parentId ? thread.messageTree.nodes[parentId] : undefined;
      const prompt =
        node?.role === 'user'
          ? builtPromptFromStoreContent(node.content)
          : undefined;

      await startTurn(thread.id, {userPrompt: prompt?.httpParts, parentId});
    },
    [startTurn, activeThreadRef]
  );

  const onCancel = useCallback(async () => {
    const threadId = activeThreadRef.current?.id;
    if (threadId) {
      abortThread(threadId);
    }
  }, [abortThread, activeThreadRef]);

  return {onNew, onEdit, onReload, onCancel};
}

/** extractText extracts plain text from an AUI `AppendMessage`'s content parts. */
function extractText(message: AppendMessage): string {
  return (message.content as readonly {type: string; text?: string}[])
    .filter(p => p.type === 'text')
    .map(p => p.text ?? '')
    .join('\n\n');
}

/**
 * withPromptContext prepends nav/page context parts (URL, entity/project/run
 * scope, page-content slices) to the user's prompt. Applied once per user
 * message at the runtime boundary so every entry point (composer, suggestions,
 * any future quick-action) carries the same context.
 */
function withPromptContext(
  userBuilt: BuiltPrompt,
  params: PromptContextParams
): BuiltPrompt {
  const contextParts = buildPromptContextParts(params);
  return builtPromptFromStoreContent([
    ...contextParts,
    ...userBuilt.storeParts,
  ]);
}

/**
 * readBuiltPrompt derives a {@link BuiltPrompt} from an AUI `AppendMessage`.
 * AUI `content` is the sole source of truth: the composer emits structured
 * parts (text + `data` parts for wandb mentions)
 */
function readBuiltPrompt(message: AppendMessage): BuiltPrompt | null {
  const storeParts = auiContentToStoreParts(
    message.content as ReadonlyArray<{type: string; [k: string]: unknown}>
  );
  if (storeParts.length === 0) {
    return null;
  }
  return builtPromptFromStoreContent(storeParts);
}

function builtPromptFromText(text: string): BuiltPrompt {
  return {
    httpParts: text,
    storeParts: [{type: 'text', text, status: 'complete'}],
    plainText: text,
  };
}

function builtPromptFromStoreContent(parts: ContentPart[]): BuiltPrompt {
  const httpParts: PromptPart[] = [];
  const plainTextChunks: string[] = [];

  for (const p of parts) {
    switch (p.type) {
      case 'text':
        httpParts.push({type: 'text', text: p.text});
        plainTextChunks.push(p.text);
        break;
      case 'image':
        httpParts.push({
          type: 'image',
          media_type: p.mediaType,
          base64_data: p.base64Data,
        });
        break;
      case 'wandb-entity':
        httpParts.push({type: 'wandb_entity', entity_name: p.entityName});
        break;
      case 'wandb-project':
        httpParts.push({
          type: 'wandb_project',
          entity_name: p.entityName,
          project_name: p.projectName,
        });
        break;
      case 'wandb-run':
        httpParts.push({
          type: 'wandb_run',
          entity_name: p.entityName,
          project_name: p.projectName,
          run_name: p.runName,
        });
        plainTextChunks.push(`@${p.runDisplayName ?? p.runName}`);
        break;
      case 'wandb-call':
        httpParts.push({
          type: 'wandb_call',
          entity_name: p.entityName,
          project_name: p.projectName,
          call_id: p.callId,
        });
        break;
      case 'wandb-web-page-state':
        httpParts.push({
          type: 'wandb_web_page_state',
          url: p.url,
          entity_name: p.entityName,
          project_name: p.projectName,
          // NOTE: the BE page-state schema accepts only workspace content, so
          // report entries drop here until that schema gains a report variant.
          content: p.content?.filter(isWorkspacePageContent).map(c => ({
            type: 'workspace',
            view_id: c.viewId,
            view_name: c.viewName,
            view_type: c.viewType,
            view_scope: c.viewScope,
            run_count_total: c.runCountTotal,
            run_count_filtered: c.runCountFiltered,
            run_count_visualized: c.runCountVisualized,
            run_filters_expression: c.runFiltersExpression,
            panel_search_query: c.panelSearchQuery,
            run_selector_grouping: c.runSelectorGrouping,
          })),
        });
        break;
      case 'unknown':
        // Unknown parts are dropped on retransmission.
        // Silently dropping now, but can add logs if needed.
        break;
    }
  }
  return {
    httpParts: httpParts.length > 0 ? httpParts : '',
    storeParts: parts,
    plainText: plainTextChunks.join('').trim(),
  };
}

function isWorkspacePageContent(
  content: PagePromptContent
): content is WorkspacePageContent {
  return content.type === 'workspace';
}
